import type {
  DataLifecycleAdministrationSnapshot,
  DataLifecycleWorkRegister,
  DataProcessingReviewRegister,
} from "@reminder-tracker/backend-contracts";
import { useMutation, useQuery } from "convex/react";
import { useState } from "react";

import {
  decideDataProcessingReview,
  getDataLifecycleAdministrationSnapshot,
  getDataLifecycleWorkItems,
  getDataProcessingReviews,
  recordDataLifecycleExternalEvidence,
  retryDataLifecycleMilestone,
} from "../../backend/functions";
import {
  AdministrationImpactPanel,
  AdministrationPageHeader,
  AdministrationStatusSummary,
  AdministrationWorkSurface,
} from "../../ui/administration-surface";

function newCorrelationId(): string {
  if (typeof globalThis.crypto.randomUUID !== "function") {
    throw new Error("A secure correlation generator is unavailable in this browser.");
  }
  return `corr_${globalThis.crypto.randomUUID().replaceAll("-", "")}`;
}

export function DataLifecycleAdministrationView({
  snapshot,
  workItems = [],
  processingReviews = [],
  onReviewDecision,
  reviewMessage,
  onRetry,
  onExternalEvidence,
  operationMessage,
}: Readonly<{
  snapshot: DataLifecycleAdministrationSnapshot;
  workItems?: DataLifecycleWorkRegister["items"];
  processingReviews?: DataProcessingReviewRegister["items"];
  onReviewDecision?: (casePublicId: string, decision: "accepted" | "refused") => Promise<void>;
  reviewMessage?: string | null;
  onRetry?: (item: DataLifecycleWorkRegister["items"][number]) => Promise<void>;
  onExternalEvidence?: (
    item: DataLifecycleWorkRegister["items"][number],
    outcome: "verified" | "not-applicable",
    acknowledgementReference: string,
  ) => Promise<void>;
  operationMessage?: string | null;
}>): React.JSX.Element {
  const [references, setReferences] = useState<Record<string, string>>({});
  const schedulerLabel =
    snapshot.scheduler.state === "healthy"
      ? "Healthy"
      : snapshot.scheduler.state === "delayed"
        ? "Delayed"
        : snapshot.scheduler.state === "stalled"
          ? "Stalled"
          : "Not enough evidence";
  const measures = [
    ["Due", snapshot.counts.due],
    ["Completed", snapshot.counts.completed],
    ["Remaining", snapshot.counts.remaining],
    ["Overdue", snapshot.counts.overdue],
    ["Exceptions", snapshot.counts.exceptions],
    ["Retryable failures", snapshot.counts.retryableFailures],
  ] as const;
  const needsAction =
    snapshot.counts.overdue + snapshot.counts.exceptions + snapshot.counts.retryableFailures;
  return (
    <section className="service-operations">
      <AdministrationPageHeader
        title="Data lifecycle"
        purpose="Review value-free disposal deadlines, processing progress and recovery assurance."
        meta={<span className="environment-badge">{snapshot.window}</span>}
      />
      <AdministrationStatusSummary
        items={[
          {
            label: "Needs action",
            tone: "action",
            value: needsAction,
            title: "Overdue, exception or retry work",
            description: "Review these value-free lifecycle items first.",
          },
          {
            label: "Monitor",
            tone: "monitor",
            value: snapshot.counts.remaining,
            title: "Remaining lifecycle work",
            description: "Continue monitoring work that has not completed.",
          },
          {
            label: "Healthy",
            tone: "healthy",
            value: snapshot.counts.completed,
            title: "Completed lifecycle work",
            description: "Completed milestones retain their audit evidence.",
          },
        ]}
      />
      <AdministrationImpactPanel
        label="Privacy boundary"
        title={
          needsAction > 0
            ? "Lifecycle work needs review without exposing customer content"
            : "No overdue, exception or retry work is recorded"
        }
        description="Customer identifiers and content are not available here. Review the value-free work register before retrying or recording external evidence."
        action={
          needsAction > 0 ? (
            <a className="secondary-action" href="#processing-recovery-title">
              Review lifecycle work
            </a>
          ) : undefined
        }
        tone={needsAction > 0 ? "action" : snapshot.counts.remaining > 0 ? "monitor" : "healthy"}
      />
      <p className={`evidence-state ${snapshot.scheduler.state}`} role="status">
        <strong>{schedulerLabel}</strong>
        <span>Customer identifiers and content are not available in this view.</span>
      </p>
      <details className="inline-details supporting-lifecycle-evidence">
        <summary id="lifecycle-deadlines-title">Overview counts and deadlines</summary>
        <dl className="operations-measures">
          {measures.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </details>
      <AdministrationWorkSurface
        id="processing-recovery-title"
        title="Processing and recovery"
        purpose="Review only the lifecycle milestones that need an operator decision or a safe retry."
        state={{
          label:
            workItems.length === 0
              ? "No work needs attention"
              : `${workItems.length} ${workItems.length === 1 ? "item" : "items"} to review`,
          tone: workItems.length === 0 ? "healthy" : "action",
        }}
        consequence="A retry resumes one failed milestone. Recording external evidence changes only that milestone's audit outcome; it does not expose or recreate customer content."
      >
        {workItems.length === 0 ? (
          <p>No lifecycle work currently needs operational attention.</p>
        ) : (
          <ul className="decision-register">
            {workItems.map((item) => {
              const itemKey = `${item.operationPublicId}-${item.milestoneKind}`;
              const canRecord =
                item.milestoneKind === "processor-data-reconciled" ||
                item.milestoneKind === "backup-reconciliation-verified";
              return (
                <li className="decision-row" key={itemKey}>
                  <div className="decision-row-copy">
                    <span className="decision-state">
                      {lifecycleStateLabel(item.milestoneState)}
                    </span>
                    <h3>{item.safeLabel}</h3>
                    <p>{item.operationType.replaceAll("-", " ")}</p>
                    <p className="decision-boundary">
                      Review the recorded milestone before applying a retry or external evidence
                      outcome.
                    </p>
                  </div>
                  <div className="decision-actions">
                    {item.milestoneState === "retryable-failure" && onRetry !== undefined ? (
                      <button
                        className="primary-action"
                        type="button"
                        onClick={() => void onRetry(item)}
                      >
                        Retry this milestone
                      </button>
                    ) : null}
                    {canRecord && onExternalEvidence !== undefined ? (
                      <span className="external-evidence-decision">
                        <label>
                          Evidence reference
                          <input
                            value={references[itemKey] ?? ""}
                            onChange={(event) =>
                              setReferences((current) => ({
                                ...current,
                                [itemKey]: event.target.value,
                              }))
                            }
                          />
                        </label>
                        <button
                          className="primary-action"
                          type="button"
                          disabled={(references[itemKey] ?? "").trim().length < 6}
                          onClick={() =>
                            void onExternalEvidence(item, "verified", references[itemKey] ?? "")
                          }
                        >
                          Record verified evidence
                        </button>
                        <button
                          className="caution-action"
                          type="button"
                          disabled={(references[itemKey] ?? "").trim().length < 6}
                          onClick={() =>
                            void onExternalEvidence(
                              item,
                              "not-applicable",
                              references[itemKey] ?? "",
                            )
                          }
                        >
                          Record not applicable
                        </button>
                      </span>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {operationMessage === undefined || operationMessage === null ? null : (
          <p role="status">{operationMessage}</p>
        )}
      </AdministrationWorkSurface>
      <AdministrationWorkSurface
        id="policy-exceptions-title"
        title="Policy and exception decisions"
        purpose="Review an objection or restriction without viewing customer content."
        state={{
          label:
            processingReviews.length === 0
              ? "No decisions due"
              : `${processingReviews.length} ${processingReviews.length === 1 ? "decision" : "decisions"} due`,
          tone: processingReviews.length === 0 ? "healthy" : "action",
        }}
        consequence="Customer choice is already paused while review is open. Applying accepts it; refusal is only for mandatory processing and must be chosen deliberately."
      >
        {processingReviews.length === 0 ? (
          <p>No processing choices currently need review.</p>
        ) : (
          <ul className="decision-register">
            {processingReviews.map((review) => (
              <li className="decision-row" key={review.casePublicId}>
                <div className="decision-row-copy">
                  <span className="decision-state">Decision due</span>
                  <h3>
                    {review.right === "objection" ? "Objection request" : "Restriction request"}
                  </h3>
                  <p>{review.purpose.replaceAll("-", " ")}</p>
                  <p className="decision-boundary">
                    Customer choice remains paused until this decision is recorded.
                  </p>
                </div>
                {onReviewDecision === undefined ? null : (
                  <span className="decision-actions">
                    <button
                      className="primary-action"
                      type="button"
                      onClick={() => void onReviewDecision(review.casePublicId, "accepted")}
                    >
                      Apply request
                    </button>{" "}
                    <button
                      className="caution-action"
                      type="button"
                      onClick={() => void onReviewDecision(review.casePublicId, "refused")}
                    >
                      Review refusal for mandatory processing
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {reviewMessage === undefined || reviewMessage === null ? null : (
          <p role="status">{reviewMessage}</p>
        )}
      </AdministrationWorkSurface>
    </section>
  );
}

function lifecycleStateLabel(
  state: DataLifecycleWorkRegister["items"][number]["milestoneState"],
): string {
  if (state === "retryable-failure") return "Retry available";
  if (state === "blocked-exception") return "Exception review required";
  if (state === "verified") return "Evidence verified";
  if (state === "not-applicable") return "Recorded as not applicable";
  if (state === "running") return "Processing";
  return "Awaiting evidence";
}

export function ProtectedDataLifecycleAdministration(): React.JSX.Element {
  const [correlationId] = useState(newCorrelationId);
  const [window] = useState<"today" | "7-days" | "30-days">("7-days");
  const snapshot = useQuery(getDataLifecycleAdministrationSnapshot, { correlationId, window });
  const work = useQuery(getDataLifecycleWorkItems, { correlationId });
  const reviews = useQuery(getDataProcessingReviews, { correlationId });
  const decideReview = useMutation(decideDataProcessingReview);
  const retryMilestone = useMutation(retryDataLifecycleMilestone);
  const recordEvidence = useMutation(recordDataLifecycleExternalEvidence);
  const [reviewMessage, setReviewMessage] = useState<string | null>(null);
  const [operationMessage, setOperationMessage] = useState<string | null>(null);
  if (snapshot === undefined || work === undefined || reviews === undefined)
    return <p aria-live="polite">Reading lifecycle evidence…</p>;
  return (
    <DataLifecycleAdministrationView
      snapshot={snapshot}
      workItems={work.items}
      processingReviews={reviews.items}
      reviewMessage={reviewMessage}
      operationMessage={operationMessage}
      onRetry={async (item) => {
        setOperationMessage("Scheduling the bounded retry…");
        try {
          await retryMilestone({
            operationPublicId: item.operationPublicId,
            milestoneKind: item.milestoneKind,
            commandKey: `cmd_${globalThis.crypto.randomUUID().replaceAll("-", "")}`,
            correlationId: newCorrelationId(),
          });
          setOperationMessage("The lifecycle check is queued for retry.");
        } catch {
          setOperationMessage("This lifecycle check could not be retried. Refresh and try again.");
        }
      }}
      onExternalEvidence={async (item, outcome, acknowledgementReference) => {
        if (
          item.milestoneKind !== "processor-data-reconciled" &&
          item.milestoneKind !== "backup-reconciliation-verified"
        )
          return;
        setOperationMessage("Recording the content-free external evidence…");
        try {
          const result = await recordEvidence({
            operationPublicId: item.operationPublicId,
            milestoneKind: item.milestoneKind,
            outcome,
            acknowledgementReference,
            commandKey: `cmd_${globalThis.crypto.randomUUID().replaceAll("-", "")}`,
            correlationId: newCorrelationId(),
          });
          setOperationMessage(
            result.operationCompleted
              ? "All applicable lifecycle checks are complete."
              : "The external lifecycle check is recorded.",
          );
        } catch {
          setOperationMessage(
            "The evidence could not be recorded. Check the reference and try again.",
          );
        }
      }}
      onReviewDecision={async (casePublicId, decision) => {
        setReviewMessage("Recording the value-free review decision…");
        try {
          const result = await decideReview({
            casePublicId,
            decision,
            reason:
              decision === "accepted"
                ? "customer-choice-applied"
                : "mandatory-rights-processing-required",
            commandKey: `cmd_${globalThis.crypto.randomUUID().replaceAll("-", "")}`,
            correlationId: newCorrelationId(),
          });
          setReviewMessage(
            result.state === "completed"
              ? "The customer choice is applied."
              : "The request was refused because mandatory processing remains required.",
          );
        } catch {
          setReviewMessage("This review decision could not be recorded. Refresh and try again.");
        }
      }}
    />
  );
}
